import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Clock,
  Color,
  Group,
  IcosahedronGeometry,
  Line,
  LineBasicMaterial,
  Mesh,
  MeshBasicMaterial,
  PerspectiveCamera,
  Points,
  Scene,
  ShaderMaterial,
  SphereGeometry,
  WebGLRenderer,
} from 'three';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { simplexNoise3D } from './glsl';
import { damp, sceneState, type ServiceKey } from './state';
import { scrollState } from '../lib/scroll';

/**
 * The persistent page backdrop: a liquid "core" whose surface is displaced by
 * domain-warped simplex noise, wrapped in orbital rings and a particle field.
 *
 * The core travels with the story. Each section declares a pose; while the
 * visitor scrolls, the scene blends between the pose of the section on screen
 * and the next one, so the 3D object always sits opposite the copy it frames.
 */

type Pose = {
  /** Horizontal position as a fraction of the visible half-width. */
  x: number;
  y: number;
  z: number;
  scale: number;
  amp: number;
  freq: number;
  speed: number;
  rings: number;
  lines: number;
  /** Mobile (portrait viewport) overrides. */
  mx?: number;
  my?: number;
  mscale?: number;
};

const POSES: Record<string, Pose> = {
  top: {
    x: 0.46,
    y: 0.04,
    z: 0,
    scale: 1,
    amp: 0.22,
    freq: 1.05,
    speed: 0.2,
    rings: 1,
    lines: 1,
    mx: 0.1,
    my: 0.44,
    mscale: 0.62,
  },
  services: {
    x: -0.52,
    y: -0.02,
    z: -0.4,
    scale: 0.9,
    amp: 0.3,
    freq: 1.2,
    speed: 0.24,
    rings: 0.35,
    lines: 0.8,
    mx: 0.72,
    my: 0.74,
    mscale: 0.28,
  },
  work: {
    x: 0.62,
    y: 0.58,
    z: -3,
    scale: 0.42,
    amp: 0.36,
    freq: 1.4,
    speed: 0.3,
    rings: 0,
    lines: 0.4,
    mx: 0.6,
    my: 0.7,
    mscale: 0.3,
  },
  process: {
    x: 0.36,
    y: 0.02,
    z: -1.4,
    scale: 1.25,
    amp: 0.16,
    freq: 0.75,
    speed: 0.12,
    rings: 0.2,
    lines: 1,
    mx: 0,
    my: 0.1,
    mscale: 0.8,
  },
  studio: {
    x: 0.86,
    y: -0.7,
    z: -4,
    scale: 0.26,
    amp: 0.3,
    freq: 1.2,
    speed: 0.2,
    rings: 0,
    lines: 0.5,
    mx: 0.78,
    my: 0.82,
    mscale: 0.16,
  },
  contact: {
    x: 0,
    y: 0,
    z: -0.6,
    scale: 1.08,
    amp: 0.34,
    freq: 1.1,
    speed: 0.26,
    rings: 1,
    lines: 1,
    mx: 0,
    my: -0.05,
    mscale: 0.6,
  },
  colophon: {
    x: 0,
    y: 0.62,
    z: -3,
    scale: 0.55,
    amp: 0.24,
    freq: 1,
    speed: 0.18,
    rings: 0.5,
    lines: 0.8,
    mx: 0,
    my: 0.6,
    mscale: 0.45,
  },
};
const ORDER = [
  'top',
  'services',
  'work',
  'process',
  'studio',
  'contact',
  'colophon',
];

/** Each service gets its own surface character: calm, precise, playful. */
const SERVICE_MORPH: Record<
  ServiceKey,
  Pick<Pose, 'amp' | 'freq' | 'speed'>
> = {
  webapps: { amp: 0.16, freq: 0.8, speed: 0.14 },
  eshops: { amp: 0.09, freq: 3.1, speed: 0.32 },
  games: { amp: 0.52, freq: 1.7, speed: 0.55 },
};

const ACCENT = new Color('#5fb8ab');
const DEEP = new Color('#325653');
const BASE = new Color('#0d1311');
const MINT = new Color('#cfe9df');

const blobVertex = /* glsl */ `
  uniform float uPhase;
  uniform float uAmp;
  uniform float uFreq;
  uniform float uEnergy;
  varying vec3 vNormal;
  varying vec3 vView;
  varying float vField;

  ${simplexNoise3D}

  float field(vec3 p) {
    vec3 q = p * uFreq;
    float warp = snoise(q * 0.55 + vec3(0.0, uPhase * 0.6, 0.0));
    float n = snoise(q + warp * 0.65 + vec3(uPhase));
    n += 0.16 * snoise(q * 2.1 - vec3(uPhase * 1.4));
    return n;
  }

  vec3 displaced(vec3 p) {
    return p * (1.0 + field(p) * (uAmp + uEnergy * 0.14));
  }

  void main() {
    vec3 p = normalize(position);
    vec3 helper = abs(p.y) > 0.99 ? vec3(1.0, 0.0, 0.0) : vec3(0.0, 1.0, 0.0);
    vec3 tangent = normalize(cross(p, helper));
    vec3 bitangent = normalize(cross(p, tangent));

    // Recompute normals from the displaced neighbourhood so light follows the ripples.
    float e = 0.012;
    vec3 d0 = displaced(p);
    vec3 d1 = displaced(normalize(p + tangent * e));
    vec3 d2 = displaced(normalize(p + bitangent * e));
    vec3 n = normalize(cross(d1 - d0, d2 - d0));
    if (dot(n, p) < 0.0) n = -n;

    vField = field(p);
    vec4 mv = modelViewMatrix * vec4(d0, 1.0);
    vView = -mv.xyz;
    vNormal = normalize(normalMatrix * n);
    gl_Position = projectionMatrix * mv;
  }
`;

const blobFragment = /* glsl */ `
  uniform vec3 uAccent;
  uniform vec3 uDeep;
  uniform vec3 uBase;
  uniform vec3 uMint;
  uniform float uLines;
  uniform float uPhase;
  varying vec3 vNormal;
  varying vec3 vView;
  varying float vField;

  void main() {
    vec3 N = normalize(vNormal);
    vec3 V = normalize(vView);
    float facing = clamp(dot(N, V), 0.0, 1.0);
    float fres = pow(1.0 - facing, 2.4);

    vec3 L = normalize(vec3(-0.45, 0.75, 0.55));
    float diff = clamp(dot(N, L), 0.0, 1.0);
    float spec = pow(clamp(dot(N, normalize(L + V)), 0.0, 1.0), 56.0);

    // Two-tone thin-film shimmer, held inside the brand's teal family.
    float film = 0.5 + 0.5 * sin(6.2831 * (fres * 0.85 + vField * 0.3 + uPhase * 0.05));
    vec3 sheen = mix(uAccent, uMint, film * 0.7);

    vec3 col = mix(uBase, uDeep, 0.25 + diff * 0.55);
    col += sheen * fres * 1.15;
    col += uMint * spec * 0.55;

    // Topographic contour lines, one pixel wide at any zoom.
    float h = vField * 5.5;
    float d = abs(fract(h - 0.5) - 0.5) / max(fwidth(h), 1e-4);
    float line = 1.0 - min(d, 1.0);
    col += uAccent * line * uLines * (0.18 + fres * 0.7);

    gl_FragColor = vec4(col, 1.0);
  }
`;

const dustVertex = /* glsl */ `
  attribute float aSeed;
  uniform float uTime;
  uniform float uPixelRatio;
  uniform float uIntro;
  varying float vAlpha;

  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    float twinkle = 0.55 + 0.45 * sin(uTime * (0.6 + aSeed * 1.6) + aSeed * 40.0);
    vAlpha = twinkle * uIntro * smoothstep(-24.0, -4.0, mv.z);
    gl_PointSize = (1.2 + aSeed * 2.4) * uPixelRatio * (9.0 / -mv.z);
    gl_Position = projectionMatrix * mv;
  }
`;

const dustFragment = /* glsl */ `
  uniform vec3 uColor;
  varying float vAlpha;

  void main() {
    float d = length(gl_PointCoord - 0.5);
    float a = smoothstep(0.5, 0.0, d);
    gl_FragColor = vec4(uColor, a * vAlpha * 0.55);
  }
`;

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const smoothstep = (a: number, b: number, v: number) => {
  const t = Math.min(1, Math.max(0, (v - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

export class BackdropScene {
  private renderer: WebGLRenderer;
  private scene = new Scene();
  private camera = new PerspectiveCamera(35, 1, 0.1, 60);
  private clock = new Clock();
  private core = new Group();
  private blob: Mesh<BufferGeometry, ShaderMaterial>;
  private rings: { line: Line; sat: Mesh; speed: number; tilt: number }[] = [];
  private ringMaterial: LineBasicMaterial;
  private satMaterial: MeshBasicMaterial;
  private dust: Points<BufferGeometry, ShaderMaterial>;
  private raf = 0;
  private sections: { id: string; top: number }[] = [];
  private resizeObserver: ResizeObserver;
  private reduced: boolean;

  // Smoothed live values.
  private phase = 0;
  private intro = 0;
  private energy = 0;
  private live = { ...POSES.top!, service: 0 };

  constructor(
    private canvas: HTMLCanvasElement,
    opts: { reduced: boolean }
  ) {
    this.reduced = opts.reduced;
    const small = window.innerWidth < 768;

    this.renderer = new WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));

    this.camera.position.set(0, 0, 6);

    // Core blob. Indexed geometry keeps the per-vertex noise cost down.
    let geo: BufferGeometry = new IcosahedronGeometry(1, small ? 36 : 72);
    geo.deleteAttribute('normal');
    geo.deleteAttribute('uv');
    geo = mergeVertices(geo);
    this.blob = new Mesh(
      geo,
      new ShaderMaterial({
        vertexShader: blobVertex,
        fragmentShader: blobFragment,
        uniforms: {
          uPhase: { value: 0 },
          uAmp: { value: POSES.top!.amp },
          uFreq: { value: POSES.top!.freq },
          uEnergy: { value: 0 },
          uLines: { value: 1 },
          uAccent: { value: ACCENT },
          uDeep: { value: DEEP },
          uBase: { value: BASE },
          uMint: { value: MINT },
        },
      })
    );
    this.core.add(this.blob);

    // Orbital rings with a satellite riding each one.
    this.ringMaterial = new LineBasicMaterial({
      color: ACCENT,
      transparent: true,
      opacity: 0,
    });
    this.satMaterial = new MeshBasicMaterial({
      color: MINT,
      transparent: true,
      opacity: 0,
    });
    const ringSpecs = [
      { r: 1.62, tilt: 1.18, speed: 0.32 },
      { r: 1.9, tilt: -0.92, speed: -0.22 },
    ];
    for (const spec of ringSpecs) {
      const pts = new Float32Array(257 * 3);
      for (let i = 0; i <= 256; i++) {
        const a = (i / 256) * Math.PI * 2;
        pts[i * 3] = Math.cos(a) * spec.r;
        pts[i * 3 + 1] = Math.sin(a) * spec.r;
      }
      const lg = new BufferGeometry();
      lg.setAttribute('position', new BufferAttribute(pts, 3));
      const line = new Line(lg, this.ringMaterial);
      line.rotation.x = spec.tilt;
      const sat = new Mesh(new SphereGeometry(0.035, 12, 12), this.satMaterial);
      line.add(sat);
      sat.userData.r = spec.r;
      this.core.add(line);
      this.rings.push({ line, sat, speed: spec.speed, tilt: spec.tilt });
    }
    this.scene.add(this.core);

    // Particle field, a loose shell around the whole scene.
    const count = small ? 700 : 1600;
    const pos = new Float32Array(count * 3);
    const seed = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      const r = 4 + Math.random() * 12;
      const th = Math.random() * Math.PI * 2;
      const ph = Math.acos(2 * Math.random() - 1);
      pos[i * 3] = r * Math.sin(ph) * Math.cos(th);
      pos[i * 3 + 1] = r * Math.sin(ph) * Math.sin(th) * 0.7;
      pos[i * 3 + 2] = r * Math.cos(ph) - 4;
      seed[i] = Math.random();
    }
    const dg = new BufferGeometry();
    dg.setAttribute('position', new BufferAttribute(pos, 3));
    dg.setAttribute('aSeed', new BufferAttribute(seed, 1));
    this.dust = new Points(
      dg,
      new ShaderMaterial({
        vertexShader: dustVertex,
        fragmentShader: dustFragment,
        transparent: true,
        depthWrite: false,
        blending: AdditiveBlending,
        uniforms: {
          uTime: { value: 0 },
          uPixelRatio: { value: this.renderer.getPixelRatio() },
          uIntro: { value: 0 },
          uColor: { value: MINT },
        },
      })
    );
    this.scene.add(this.dust);

    this.resize();
    window.addEventListener('resize', this.resize);
    this.resizeObserver = new ResizeObserver(this.measureSections);
    this.resizeObserver.observe(document.body);

    if (this.reduced) {
      // Static composition: everything in place, one frame per resize.
      this.intro = 1;
      this.applyPose(POSES.top!, 0, 1);
    } else {
      this.core.scale.setScalar(0.0001);
    }
  }

  /** Compile shaders and paint the first frame, so the reveal never stutters. */
  async warmUp() {
    this.renderer.compile(this.scene, this.camera);
    this.renderer.render(this.scene, this.camera);
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
  }

  start() {
    if (this.reduced) {
      this.renderer.render(this.scene, this.camera);
      return;
    }
    this.clock.start();
    const tick = () => {
      this.raf = requestAnimationFrame(tick);
      this.update(Math.min(this.clock.getDelta(), 1 / 20));
      this.renderer.render(this.scene, this.camera);
    };
    tick();
  }

  private measureSections = () => {
    const y = window.scrollY;
    this.sections = ORDER.map((id) => {
      const el = document.getElementById(id);
      return { id, top: el ? el.getBoundingClientRect().top + y : Infinity };
    }).filter((s) => Number.isFinite(s.top));
  };

  private resize = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.measureSections();
    if (this.reduced) this.renderer.render(this.scene, this.camera);
  };

  /** Which two poses are we between, and how far along? */
  private targetPose() {
    const vh = window.innerHeight;
    const anchor = window.scrollY + vh * 0.5;
    const s = this.sections;
    if (!s.length) return { pose: POSES.top!, service: 0 };

    // Blend while the next section rises from the viewport bottom to ~40%.
    const from = (top: number) => top - vh * 0.5;
    const to = (top: number) => top + vh * 0.1;

    let i = 0;
    while (i < s.length - 1 && anchor >= to(s[i + 1]!.top)) i++;
    const a = POSES[s[i]!.id]!;
    const next = s[i + 1];
    if (!next) return { pose: a, service: s[i]!.id === 'services' ? 1 : 0 };

    const t = smoothstep(from(next.top), to(next.top), anchor);
    const b = POSES[next.id]!;
    const pose = {} as Pose;
    (Object.keys(a) as (keyof Pose)[]).forEach((k) => {
      pose[k] = lerp(a[k] ?? 0, b[k] ?? a[k] ?? 0, t);
    });
    const service =
      (s[i]!.id === 'services' ? 1 - t : 0) + (next.id === 'services' ? t : 0);
    return { pose, service };
  }

  private applyPose(p: Pose, dt: number, lambda: number) {
    const portrait = this.camera.aspect < 0.9;
    // Visible extent at the pose's own depth, so distant poses still land
    // where they are aimed instead of drifting toward the centre.
    const halfH =
      Math.tan((this.camera.fov * Math.PI) / 360) *
      (this.camera.position.z - p.z);
    const halfW = halfH * this.camera.aspect;
    const tx = (portrait ? (p.mx ?? p.x) : p.x) * halfW;
    const ty = (portrait ? (p.my ?? p.y) : p.y) * halfH;
    const ts = portrait ? (p.mscale ?? p.scale) : p.scale;

    const l = this.live;
    const k = dt === 0 ? 1 : 1 - Math.exp(-lambda * dt);
    l.x += (tx - l.x) * k;
    l.y += (ty - l.y) * k;
    l.z += (p.z - l.z) * k;
    l.scale += (ts - l.scale) * k;
    l.rings += (p.rings - l.rings) * k;
    l.lines += (p.lines - l.lines) * k;

    this.core.position.set(l.x, l.y, l.z);
    this.core.scale.setScalar(Math.max(0.0001, l.scale * this.intro));
    this.ringMaterial.opacity = l.rings * this.intro * 0.4;
    this.satMaterial.opacity = l.rings * this.intro * 0.9;
    this.blob.material.uniforms.uLines!.value = l.lines;
  }

  private update(dt: number) {
    const t = this.clock.elapsedTime;
    const { pose, service } = this.targetPose();

    // Intro: the core blooms out of nothing once the loader lifts.
    this.intro = damp(this.intro, sceneState.revealed ? 1 : 0, 2.4, dt);

    // Service hover morphs the surface character while the list is on screen.
    let amp = pose.amp;
    let freq = pose.freq;
    let speed = pose.speed;
    this.live.service = damp(this.live.service, service, 4, dt);
    const key = sceneState.service;
    if (key && this.live.service > 0.01) {
      const m = SERVICE_MORPH[key];
      amp = lerp(amp, m.amp, this.live.service);
      freq = lerp(freq, m.freq, this.live.service);
      speed = lerp(speed, m.speed, this.live.service);
    }

    const u = this.blob.material.uniforms;
    this.live.amp = damp(this.live.amp, amp, 3, dt);
    this.live.freq = damp(this.live.freq, freq, 3, dt);
    this.live.speed = damp(this.live.speed, speed, 3, dt);
    this.phase += dt * this.live.speed;

    // Scroll velocity agitates the surface, then it settles.
    const v = Math.min(1, Math.abs(scrollState.velocity) / 45);
    this.energy = damp(this.energy, v, v > this.energy ? 6 : 1.6, dt);

    u.uPhase!.value = this.phase;
    u.uAmp!.value = this.live.amp;
    u.uFreq!.value = this.live.freq;
    u.uEnergy!.value = this.energy;

    this.applyPose(pose, dt, 2.6);

    // Slow spin, with a lean toward the pointer.
    const px = sceneState.pointer.x;
    const py = sceneState.pointer.y;
    this.blob.rotation.y += dt * 0.08;
    this.core.rotation.x = damp(this.core.rotation.x, -py * 0.22, 2, dt);
    this.core.rotation.z = damp(this.core.rotation.z, px * 0.12, 2, dt);

    for (const r of this.rings) {
      r.line.rotation.z += dt * r.speed;
      const a = t * r.speed * 2.2;
      r.sat.position.set(
        Math.cos(a) * r.sat.userData.r,
        Math.sin(a) * r.sat.userData.r,
        0
      );
    }

    // Camera drifts with the pointer for depth parallax against the dust.
    this.camera.position.x = damp(this.camera.position.x, px * 0.35, 1.8, dt);
    this.camera.position.y = damp(this.camera.position.y, py * 0.22, 1.8, dt);
    this.camera.lookAt(0, 0, 0);

    const du = this.dust.material.uniforms;
    du.uTime!.value = t;
    du.uIntro!.value = this.intro;
    this.dust.rotation.y = t * 0.012 + window.scrollY * 0.00012;
    this.dust.rotation.x = window.scrollY * 0.00004;
  }

  destroy() {
    cancelAnimationFrame(this.raf);
    window.removeEventListener('resize', this.resize);
    this.resizeObserver.disconnect();
    this.scene.traverse((o) => {
      const m = o as Mesh;
      m.geometry?.dispose();
      const mat = m.material as { dispose?: () => void } | undefined;
      mat?.dispose?.();
    });
    this.renderer.dispose();
  }
}
